import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronsUpDown } from 'lucide-react'

import { useRailTooltip } from '@/components/ui/RailTooltip'
import { useOrganization } from '@/lib/org/use-organization'
import { useAnchoredMenu } from '@/lib/ui/use-anchored-menu'
import { cn } from '@/lib/utils'

export interface OrgSwitcherProps {
  /** Icon-only rail: renders the initial in a tile with the name in a tooltip. */
  collapsed?: boolean | undefined
}

/** Two letters when the name has two words ("Office - S2C" → "OS"), else one. */
function initialsOf(name: string): string {
  const words = name.split(/[\s\-–—]+/).filter(Boolean)
  const first = words[0]?.[0] ?? '?'
  const second = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return `${first}${second}`.toUpperCase()
}

/**
 * The organization row under the wordmark.
 *
 * A single-org tenant still gets the row (it is the only place the org name is
 * shown) but the menu is suppressed — there is nothing to switch to.
 */
export function OrgSwitcher({ collapsed = false }: OrgSwitcherProps) {
  const { organization, organizations, switchOrganization, loading } = useOrganization()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  // Portalled and positioned in viewport coordinates. As an `absolute` child it
  // was clipped by the sidebar's `overflow-y-auto` — and on the 72px rail that
  // meant a 72px-wide list with every company name sliced off ("Space2Code" read
  // "Space2"). The one width is deliberate: the list must be readable at both
  // sidebar widths, not sized to whichever one is on screen.
  const menuStyle = useAnchoredMenu(open, buttonRef, { width: 224, align: 'left' })

  // Above the loading return: a hook cannot live after one.
  const name = organization?.name ?? 'No organization'
  const { anchorProps, tooltip } = useRailTooltip(name, collapsed)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      // The menu is portalled to <body>, so it is NOT inside rootRef. Without
      // checking it too, pointerdown on an option closed the list and unmounted
      // the button before its click could fire — switching company silently did
      // nothing.
      const target = event.target as Node
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (loading) {
    return (
      <div
        className={cn(
          'h-10 animate-pulse rounded-control bg-sidebar-hover',
          collapsed && 'size-10',
        )}
        aria-hidden
      />
    )
  }

  const switchable = organizations.length > 1

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={!switchable}
        aria-haspopup={switchable ? 'listbox' : undefined}
        aria-expanded={switchable ? open : undefined}
        aria-label={
          switchable ? `Organization: ${name}. Switch organization` : `Organization: ${name}`
        }
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        {...anchorProps}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-control px-2 text-left transition-colors',
          switchable && 'hover:bg-sidebar-hover',
          collapsed && 'justify-center px-0',
        )}
      >
        <span className="grid size-6 shrink-0 place-items-center rounded bg-accent text-[10px] font-semibold text-fg-on-accent">
          {initialsOf(name)}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{name}</span>
            {switchable && <ChevronsUpDown className="size-4 shrink-0 text-fg-muted" aria-hidden />}
          </>
        )}
      </button>

      {/* Portalled, not a child: the sidebar is `overflow-y-auto`, which clips it. */}
      {tooltip}

      {open &&
        switchable &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            aria-label="Organizations"
            style={menuStyle}
            className="max-h-72 overflow-y-auto rounded-control border border-border bg-surface-raised py-1 shadow-lg"
          >
            {organizations.map((candidate) => {
              const isCurrent = candidate.id === organization?.id
              return (
                <li key={candidate.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    onClick={() => {
                      switchOrganization(candidate.id)
                      setOpen(false)
                      buttonRef.current?.focus()
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                  >
                    <span className="min-w-0 flex-1 truncate">{candidate.name}</span>
                    {isCurrent && <Check className="size-4 shrink-0 text-accent" aria-hidden />}
                  </button>
                </li>
              )
            })}
          </ul>,
          document.body,
        )}
    </div>
  )
}
